/**
 * Convert a saved invoice/deviz into the state shape used by Calculator/DevizPage.
 *
 * Selections are keyed by `${materialId}::${color}` where color is "Mat"
 * (base price) or one of the material's defined color names.
 *
 * @param {object} inv  - The invoice document returned by GET /invoices/:id
 * @param {Array}  materials - Current materials list.
 */
const MAT = "Mat";
const SHEETS_PREFIX = "Lungime*Coli:";

// Split a stored note "Culoare: X. Lungime*Coli: 1.5*4 + 2*3 | user note"
// back into the sheets list and the user's own note.
function parseLineNotes(raw, color) {
  let n = (raw || "").trim();
  const colorPrefix = `Culoare: ${color}`;
  if (color && n.toLowerCase().startsWith(colorPrefix.toLowerCase())) {
    n = n.slice(colorPrefix.length).replace(/^\.\s*/, "").trim();
  }
  if (!n.startsWith(SHEETS_PREFIX)) return { sheets: [], userNote: n };
  const rest = n.slice(SHEETS_PREFIX.length);
  const sep = rest.indexOf(" | ");
  const dims = sep >= 0 ? rest.slice(0, sep) : rest;
  const userNote = sep >= 0 ? rest.slice(sep + 3).trim() : "";
  const sheets = dims
    .split("+")
    .map((p) => p.trim().split("*"))
    .filter((p) => p.length === 2 && Number(p[0]) > 0 && Number(p[1]) > 0)
    .map(([length, count], i) => ({ id: `r${i}`, length: length.trim(), count: count.trim() }));
  return { sheets, userNote };
}

// Only trust the parsed sheets if they still add up to the saved m².
function sheetsMatch(sheets, width, qty) {
  if (!sheets.length) return false;
  const sum = sheets.reduce((a, s) => a + Number(s.length) * Number(width) * Number(s.count), 0);
  return Math.abs(sum - qty) < 0.01;
}

export function invoiceToFormState(inv, materials) {
  const matsById = new Map((materials || []).map((m) => [m.id, m]));
  const matsByName = new Map(
    (materials || []).map((m) => [(m.name || "").trim().toLowerCase(), m])
  );

  const resolveMaterial = (line) => {
    if (line?.material_id && matsById.has(line.material_id)) {
      return matsById.get(line.material_id);
    }
    const key = (line?.name || "").trim().toLowerCase();
    return key ? matsByName.get(key) || null : null;
  };

  // Map a stored line color to the catalog casing of the material's color,
  // falling back to "Mat" when empty or no longer defined.
  const resolveColor = (mat, rawColor) => {
    const c = (rawColor || "").trim();
    if (!c || c.toLowerCase() === MAT.toLowerCase()) return MAT;
    const found = (mat.colors || []).find(
      (x) => (x.name || "").trim().toLowerCase() === c.toLowerCase()
    );
    return found ? (found.name || "").trim() : c;
  };

  const selections = {};
  const usedColors = new Map(); // lowercase -> casing
  for (const ln of inv?.lines || []) {
    const qty = Number(ln.quantity || 0);
    if (qty <= 0) continue;
    const mat = resolveMaterial(ln);
    if (!mat) continue; // material was deleted from catalog — skip silently
    const color = resolveColor(mat, ln.color);
    usedColors.set(color.toLowerCase(), color);
    const key = `${mat.id}::${color}`;
    const hasWidth = Number(mat.width_m || 0) > 0;
    const { sheets, userNote } = parseLineNotes(ln.notes, color);
    if (hasWidth && sheetsMatch(sheets, mat.width_m, qty)) {
      selections[key] = { mode: "sheets", sheets, notes: userNote };
    } else if (hasWidth) {
      selections[key] = { mode: "direct", direct_m2: qty, notes: userNote };
    } else {
      selections[key] = { quantity: qty, notes: userNote };
    }
  }
  const colorsList = Array.from(usedColors.values());
  return {
    selections,
    selectedColors: colorsList.length ? colorsList : [MAT],
    client: {
      lastName: inv?.client_last_name || "",
      firstName: inv?.client_first_name || "",
      cui: inv?.client_cui || "",
      cnp: inv?.client_cnp || "",
      address: inv?.client_address || "",
      phone: inv?.client_phone || "",
      regCom: inv?.client_reg_com || "",
    },
    delegateName: inv?.delegate_name || "",
    discountPct: inv?.discount_pct ? String(inv.discount_pct) : "",
    advance: inv?.advance ? String(inv.advance) : "",
    includePrices: inv?.type === "deviz" ? !!inv?.include_prices : true,
    includeCodes: inv?.include_codes !== false,
  };
}
